import { npcChatText, qaFor, type Lang, type NpcAgent } from "@greg-chick/shared";

/**
 * 👀 **아이 화면 미리보기** — 마을에서 이 친구를 누르면 무엇이 보이는지.
 *
 * ## 왜 필요한가
 * 운영자는 역할·단계·문답을 고르지만 **결과를 볼 길이 없었다.** 저장하고, 게시하고,
 * 아이 화면을 열어 마을을 걸어가 친구를 눌러야 확인이 된다 — 그 사이에 하나라도 어긋나면
 * 「분명 만들었는데 안 나온다」가 되고, 어디가 틀렸는지 알 수가 없다.
 *
 * ## 판정을 다시 짜지 않는다
 * 무엇이 보일지(`qaFor`)와 창의 글(`npcChatText`)은 **웹과 같은 함수**를 쓴다.
 * 여기서 다시 짜면 그럴듯하게 동작하면서 조용히 갈린다 — 이 저장소가 반나절을 쓴 적 있는
 * 종류의 사고다(`previewParity.test.ts` 가 그래서 있다).
 *
 * 여기서 그리는 건 **모양이 아니라 내용**이다. 정확한 색·간격까지 맞추려 들지 않는다 —
 * 운영자가 알아야 하는 건 「무엇이 몇 개 보이는가」이고, 그건 판정이 정한다.
 */
export function NpcChatPreview({
  agent,
  label,
  lang,
}: {
  agent: NpcAgent;
  label: string;
  lang: Lang;
}) {
  const t = npcChatText(lang);
  const qa = qaFor(agent, lang);
  const freeChat = agent.level === 2;

  return (
    <div className="chatprev">
      <p className="chatprev__cap">
        👀 아이 화면에서 <b>{label}</b> 을(를) 누르면
      </p>
      <div className="chatprev__win">
        <div className="chatprev__head">
          🧚 {label}
          <span className="chatprev__x">✕</span>
        </div>
        <div className="chatprev__body">
          <p className="chatprev__line">안녕하세요! 무엇을 도와드릴까요?</p>
          <p className="chatprev__note">
            (인사말은 <b>[💬 말]</b> 에서 정해요)
          </p>
        </div>

        {qa.length > 0 ? (
          <div className="chatprev__btns">
            {qa.map((q) => (
              <span key={q.id} className="chatprev__chip">
                {q.q}
              </span>
            ))}
          </div>
        ) : (
          <p className="chatprev__empty">{t.empty}</p>
        )}

        {freeChat ? (
          <div className="chatprev__gate">
            <b>{t.gateTitle}</b>
            <span>{t.gateWhy}</span>
            <span className="chatprev__quiz">13 × 7 = ?</span>
          </div>
        ) : null}
      </div>
      <p className="hint">
        {qa.length === 0
          ? "지금은 물어볼 것이 하나도 안 보여요 — ✅ 한 문답이 있어야 나와요."
          : `✅ 한 문답 중 앞에서부터 ${qa.length}개가 버튼으로 보여요.`}
      </p>
    </div>
  );
}
